import React, { useEffect, useRef, useState } from "react";
import { Button, Select, message } from "antd";
import { KeyOutlined, EyeOutlined, CopyOutlined } from "@ant-design/icons";

import { controller } from "../../../../controller";
import { carrierCodeFromPolicy } from "../../lib/carrierCapabilities";
import {
  accountLabel,
  accountsFromResponse,
  defaultAccountId,
  providerIdentityRows,
} from "../../lib/portalAccounts";
import "./CarrierPortalCredential.css";

const CLIPBOARD_CLEAR_MS = 25000;

/**
 * FTC/admin view of the office's STORED portal login(s) for the selected
 * policy's carrier — so staff can log into carriers we don't scrape by hand.
 * Sits next to <CarrierContactBar> in the insurance modal.
 *
 * An office may hold several accounts on one portal: the office-wide DEFAULT
 * the bots use, plus one per dentist (an estimate only appears under the
 * account of the provider who submitted it). The default is selected on open;
 * when there is more than one account a picker lets staff switch, and the
 * chosen dentist's own claim numbers show beneath it when the office recorded
 * them on the Provider.
 *
 * Security posture (no zero-knowledge is possible — the scraper bots must decrypt
 * server-side — so we borrow the password-manager UX instead):
 *   - the password is COPIED to the clipboard and NEVER rendered;
 *   - the clipboard auto-clears ~25s later;
 *   - the revealed password lives only in a ref, never in React state / storage,
 *     and is wiped on unmount (modal close) and whenever the account changes.
 * Renders nothing when the office has no login on file for the carrier.
 */
export default function CarrierPortalCredential({ policy, officeId: officeIdProp }) {
  // Use the patient's OWN office (threaded from the modal) — NOT the header
  // selectedOffice. For an FTC/super-admin viewing a cross-office patient the
  // header office differs, and revealing by it would show the WRONG office's
  // carrier login. Fall back to selectedOffice only when no office was threaded
  // (the normal single-office Patients board, where they're equal).
  const officeId =
    officeIdProp != null && officeIdProp !== ""
      ? officeIdProp
      : typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem("selectedOffice")
      : null;
  const code = policy ? carrierCodeFromPolicy(policy) : null;

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]); // ordered, default first; metadata only
  const [selectedId, setSelectedId] = useState(null);
  const [username, setUsername] = useState(null); // shown after reveal (not the secret)
  const [revealing, setRevealing] = useState(false);
  const pwdRef = useRef(null); // transient secret — never rendered, wiped on unmount
  const clearTimer = useRef(null);

  const forgetReveal = () => {
    setUsername(null);
    pwdRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    forgetReveal();
    if (!officeId || !code) {
      setLoading(false);
      setAccounts([]);
      setSelectedId(null);
      return undefined;
    }
    setLoading(true);
    controller
      .getCarrierPortalCredential(officeId, code)
      .then((res) => {
        if (cancelled) return;
        const list = accountsFromResponse(res);
        setAccounts(list);
        setSelectedId(defaultAccountId(list));
      })
      .catch(() => {
        if (!cancelled) {
          setAccounts([]);
          setSelectedId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [officeId, code]);

  // Wipe the transient secret + cancel any pending clipboard clear on unmount.
  useEffect(
    () => () => {
      pwdRef.current = null;
      if (clearTimer.current) clearTimeout(clearTimer.current);
    },
    []
  );

  const selected = accounts.find((a) => a.id === selectedId) || null;

  // Switching accounts must drop the previous account's revealed login: the
  // username on screen and the password in the ref would otherwise belong to a
  // different dentist than the one now named on the card.
  const handleSelect = (id) => {
    forgetReveal();
    setSelectedId(id);
  };

  const handleReveal = async () => {
    if (!selected) return;
    setRevealing(true);
    try {
      const data = await controller.revealPortalCredential(officeId, selected.id);
      setUsername(data && data.username != null ? String(data.username) : "");
      pwdRef.current = data && data.password != null ? String(data.password) : "";
    } catch (e) {
      message.error("Couldn't reveal this login.");
    } finally {
      setRevealing(false);
    }
  };

  const handleCopyPassword = async () => {
    const pwd = pwdRef.current;
    if (!pwd) return;
    if (!(navigator.clipboard && navigator.clipboard.writeText)) {
      message.error("Clipboard isn't available in this browser.");
      return;
    }
    try {
      await navigator.clipboard.writeText(pwd);
      message.success("Password copied — clipboard clears in 25s");
      if (clearTimer.current) clearTimeout(clearTimer.current);
      clearTimer.current = setTimeout(() => {
        try {
          navigator.clipboard.writeText("");
        } catch (e) {
          /* best-effort */
        }
      }, CLIPBOARD_CLEAR_MS);
    } catch (e) {
      message.error("Couldn't copy the password.");
    }
  };

  if (loading || !selected) return null;

  const identityRows = providerIdentityRows(selected);
  const hasProvider = !!(selected.provider && selected.provider.name);

  return (
    <div className="carrier-cred-bar">
      <KeyOutlined className="carrier-cred-ico" />
      <span className="carrier-cred-title">
        {accounts.length > 1 ? "Stored portal logins" : "Stored portal login"}
      </span>

      {/* Which account. A picker only when there is a choice; otherwise name the
          dentist so staff know WHOSE login they are about to use. */}
      {accounts.length > 1 ? (
        <div className="carrier-cred-account">
          <span className="carrier-cred-account-k">Account</span>
          <Select
            size="small"
            value={selectedId}
            onChange={handleSelect}
            options={accounts.map((a) => ({ value: a.id, label: accountLabel(a) }))}
            popupMatchSelectWidth={false}
            aria-label="Portal account"
          />
        </div>
      ) : hasProvider ? (
        <span className="carrier-cred-account-name">{accountLabel(selected)}</span>
      ) : null}

      {/* The dentist's own claim identifiers travel with a per-provider login. */}
      {identityRows.length ? (
        <div className="carrier-cred-provider">
          {identityRows.map(([k, v]) => (
            <span key={k}>
              {k} <b>{v}</b>
            </span>
          ))}
        </div>
      ) : null}

      {username == null ? (
        <Button size="small" icon={<EyeOutlined />} loading={revealing} onClick={handleReveal}>
          Show login
        </Button>
      ) : (
        <>
          <span className="carrier-cred-user">
            Username: <b>{username || "—"}</b>
          </span>
          <Button size="small" type="primary" ghost icon={<CopyOutlined />} onClick={handleCopyPassword}>
            Copy password
          </Button>
          <span className="carrier-cred-hint">password is copied, never shown</span>
        </>
      )}
    </div>
  );
}
